import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Phone, User, ShoppingCart, MapPin, Menu, X, ChevronDown } from "lucide-react";
import { navLinks } from "../mock";
import { useCart } from "../context/CartContext";
import AnnouncementBar from "./AnnouncementBar";
import MegaMenu, { iconMap } from "./MegaMenu";

function Logo() {
  return (
    <Link to="/" className="flex items-center gap-2 shrink-0 select-none">
      <span className="font-heading font-extrabold text-2xl md:text-3xl tracking-tight text-navy">
        CYBER<span className="text-brand">FIT</span>
      </span>
      <span className="flex flex-col leading-none rounded-sm bg-brand px-1.5 py-0.5 text-white">
        <span className="font-heading font-extrabold text-[11px]">20</span>
        <span className="text-[6px] tracking-widest">YEARS</span>
      </span>
    </Link>
  );
}

export default function Header() {
  const { count, setOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header id="top" className="sticky top-0 z-50">
      <AnnouncementBar />
      <div className={`bg-white transition-shadow ${scrolled ? "shadow-md" : "shadow-sm"}`}>
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="flex items-center gap-4 py-3">
            <button
              className="lg:hidden text-navy"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
            >
              <Menu size={26} />
            </button>
            <Logo />
            <div className="hidden md:flex flex-1 max-w-xl mx-auto">
              <div className="flex w-full items-center rounded-full border border-navy/15 bg-gray-50 px-4 py-2.5 focus-within:border-brand transition-colors">
                <Search size={18} className="text-navy/50" />
                <input
                  className="w-full bg-transparent px-3 text-sm outline-none placeholder:text-navy/40"
                  placeholder="Search products..."
                />
              </div>
            </div>
            <div className="flex items-center gap-4 md:gap-5 ml-auto">
              <a href="tel:+61296206635" className="hidden lg:flex items-center gap-2 text-navy hover:text-brand transition-colors">
                <Phone size={20} />
                <span className="font-semibold text-sm">02 9620 6635</span>
              </a>
              <a href="#showroom" className="hidden sm:block text-navy hover:text-brand transition-colors" aria-label="Locations">
                <MapPin size={22} />
              </a>
              <button className="hidden sm:block text-navy hover:text-brand transition-colors" aria-label="Account">
                <User size={22} />
              </button>
              <button
                onClick={() => setOpen(true)}
                className="relative text-navy hover:text-brand transition-colors"
                aria-label="Cart"
              >
                <ShoppingCart size={22} />
                <span className="absolute -top-2 -right-2 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-brand px-1 text-[11px] font-bold text-white">
                  {count}
                </span>
              </button>
            </div>
          </div>
        </div>
        {/* Desktop nav */}
        <nav className="hidden lg:block bg-navy text-white">
          <div className="relative mx-auto max-w-7xl px-4 lg:px-8">
            <ul className="flex items-center justify-between">
              {navLinks.map((link) => (
                <li key={link.label} className="group static">
                  <a
                    href={link.href}
                    className="flex items-center gap-1 py-3.5 text-[13px] font-semibold uppercase tracking-wide text-white/90 transition-colors group-hover:text-brand"
                  >
                    {link.label}
                    {link.items && (
                      <ChevronDown size={14} className="transition-transform group-hover:rotate-180" />
                    )}
                  </a>
                  <MegaMenu link={link} />
                </li>
              ))}
            </ul>
          </div>
        </nav>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-[86%] max-w-sm overflow-y-auto bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-navy/10 p-4">
              <Logo />
              <button onClick={() => setMobileOpen(false)} aria-label="Close">
                <X size={24} className="text-navy" />
              </button>
            </div>
            <div className="p-4">
              <div className="mb-4 flex items-center rounded-full border border-navy/15 bg-gray-50 px-4 py-2.5">
                <Search size={18} className="text-navy/50" />
                <input className="w-full bg-transparent px-3 text-sm outline-none" placeholder="Search products..." />
              </div>
              <ul className="divide-y divide-navy/10">
                {navLinks.map((link) => (
                  <li key={link.label}>
                    <button
                      onClick={() => setExpanded(expanded === link.label ? null : link.label)}
                      className="flex w-full items-center justify-between py-3 text-left font-heading text-sm font-bold uppercase tracking-wide text-navy"
                    >
                      {link.label}
                      {link.items && (
                        <ChevronDown
                          size={16}
                          className={`transition-transform ${expanded === link.label ? "rotate-180 text-brand" : ""}`}
                        />
                      )}
                    </button>
                    {link.items && expanded === link.label && (
                      <div className="grid grid-cols-1 gap-1 pb-3">
                        {link.items.map((it) => {
                          const Icon = iconMap[it.icon];
                          return (
                            <a
                              key={it.label}
                              href={link.href}
                              onClick={() => setMobileOpen(false)}
                              className="flex items-center gap-3 rounded-lg px-2 py-2 text-navy/80 hover:bg-sky"
                            >
                              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-gray-100 text-navy">
                                {Icon && <Icon size={16} />}
                              </span>
                              <span className="text-sm font-semibold">{it.label}</span>
                            </a>
                          );
                        })}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
              <a href="tel:+61296206635" className="mt-5 flex items-center gap-2 font-semibold text-brand">
                <Phone size={18} /> 02 9620 6635
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
